'use client';

import { useCallback, useMemo, useState } from 'react';

import { addDays, fromIsoDate, startOfToday, toIsoDate } from '@/lib/date';
import { useGetCargoPickupDatesQuery } from '@/services/cargoApi';
import { skipToken } from '@reduxjs/toolkit/query/react';

import type { CargoLocation } from '@/types/cargo';

export type PickupDates = ReturnType<typeof usePickupDates>;

/**
 * Backend'in kabul ettiği pencere: yarın ile bugün + `PickupLookaheadDays`
 * (Hepsijet ayarında 14) arası. Bugün dahil DEĞİL — sağlayıcı aynı gün alım
 * yapmadığı için talep doğrulaması bugünü reddediyor.
 */
const PICKUP_LOOKAHEAD_DAYS = 14;

/**
 * Randevu takvimini çeker.
 *
 * Seçilebilir günlerin TEK kaynağı backend cevabıdır: GET
 * /repair-request/cargo/pickup-dates "YYYY-MM-DD" listesi döner ve takvimde bu
 * listenin dışında hiçbir gün açılmaz. İstemcide üretilen bir pencere
 * sağlayıcının kapalı olduğu günleri de seçtirir, talep de 400 ile döner.
 *
 * Liste BOŞ dönebilir; bu hata değil, o ilçe için paylaşılmış gün olmaması
 * demektir. Gün seçimi her hâlükârda zorunlu: seçilecek gün yoksa akış ödeme
 * adımına geçmez, çünkü kuryenin ne zaman geleceği belli olmadan ödeme almak
 * müşteriyi tarihsiz bir randevuya bağlar.
 */
export const usePickupDates = (
  city: CargoLocation | null,
  town: CargoLocation | null
) => {
  // Adım açıkken gün dönmesin diye referans gün bir kez sabitlenir.
  const [today] = useState(startOfToday);
  const from = useMemo(() => toIsoDate(addDays(today, 1)), [today]);
  const to = useMemo(
    () => toIsoDate(addDays(today, PICKUP_LOOKAHEAD_DAYS)),
    [today]
  );

  const { data, isFetching, isError, refetch } = useGetCargoPickupDatesQuery(
    city && town ? { city: city.name, town: town.name, from, to } : skipToken
  );

  // Sıra garanti edilmiyor; ilk/son gün takvimin gezinme sınırlarını
  // belirlediği için sıralama burada garantiye alınır. Aralık dışı bir gün
  // gelirse (sağlayıcı bugünü de sayabiliyor) talep 400 dönerdi, o yüzden
  // pencereye de burada kırpılıyor.
  const days = useMemo(
    () => [...(data ?? [])].filter((d) => d >= from && d <= to).sort(),
    [data, from, to]
  );
  const daySet = useMemo(() => new Set(days), [days]);
  const hasDays = days.length > 0;

  const isSelectable = useCallback((iso: string) => daySet.has(iso), [daySet]);

  return {
    isLoading: isFetching,
    isError,
    refetch,
    /** Backend gün paylaştı mı? Paylaşmadıysa takvim yerine bilgi metni çıkar. */
    hasDays,
    isSelectable,
    /** Takvimde gezilebilecek aralık: backend'in ilk ve son günü. */
    firstDay: hasDays ? fromIsoDate(days[0]) : null,
    lastDay: hasDays ? fromIsoDate(days[days.length - 1]) : null,
  };
};
